import * as React from 'react';
import { IGroupedComboBox, IGroupedComboBoxGroup } from 'grouped-combobox-core';
import { IFontPickerPropsBase } from '../FontPicker.types.shared';
import { pickArray, pick } from '../../common/pick';
import { GroupCreationData, GroupedComboBoxRenderers } from './createGroups.types';
import { createGroups } from './createGroups';
import { assert } from '../../common/assert';

// must keep this in sync with GroupCreationData type
const fontPickerKeys: Array<keyof IFontPickerPropsBase> = [
    'fonts',
    'mruList',
    'stringResources',
    'isSubscriber',
    'maximumMruEntries',
    'initialPinnedFontKeys',
    'allowFontInfoPane',
    'allowPinning',
];

type OnDemandGroupCreationData = {
    recreateGroups: boolean;
    data?: GroupCreationData;
};

export function useCreateGroupsOnDemand(
    props: IFontPickerPropsBase,
    setGroups: React.Dispatch<React.SetStateAction<IGroupedComboBoxGroup[]>>,
    comboBoxRef: React.RefObject<IGroupedComboBox>,
    renderers: GroupedComboBoxRenderers,
    onMenuOpened?: () => void
): () => void {
    const createGroupsOnDemand = React.useMemo<OnDemandGroupCreationData>(
        () => ({
            recreateGroups: false,
        }),
        []
    );

    const dependencies = pickArray(props, fontPickerKeys);

    React.useEffect(() => {
        // When one of these props changes...

        if (comboBoxRef.current?.isDropdownOpen) {
            createGroupsOnDemand.recreateGroups = false;

            // if the dropdown is currently open, update the groups right away
            setGroups(createGroups(pick(props, fontPickerKeys), renderers));
        } else {
            // otherwise, wait until the dropdown opens.
            createGroupsOnDemand.recreateGroups = true;
            createGroupsOnDemand.data = pick(props, fontPickerKeys);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);

    const menuOpenedCallback = React.useCallback(() => {
        const { recreateGroups, data } = createGroupsOnDemand;

        if (!recreateGroups) {
            onMenuOpened?.();
            return;
        }

        assert(data);
        setGroups(createGroups(data, renderers));

        createGroupsOnDemand.recreateGroups = false;
        createGroupsOnDemand.data = undefined;

        onMenuOpened?.();
    }, [onMenuOpened, setGroups, renderers, createGroupsOnDemand]);

    return menuOpenedCallback;
}
